import { z } from 'zod';
import { codeSchema } from '../code.js';
/** Tipos de campo del constructor de formularios (Configuración › Formularios). */
export const FORM_FIELD_TYPES = ['Text', 'Textarea', 'Number', 'Currency', 'Date', 'DateTime', 'Select', 'MultiSelect', 'Checkbox', 'Radio', 'File', 'Signature', 'User', 'Organization', 'Address', 'Email', 'Phone', 'Court'];
export const formFieldSchema = z.object({
    name: z.string().min(1),
    label: z.string().min(1),
    type: z.enum(FORM_FIELD_TYPES),
    required: z.boolean().optional(),
    options: z.array(z.string()).optional(),
    /** Opciones que vienen de un catálogo (`templates:RESPONSE`, …) en vez de `options`. */
    optionsFrom: z.string().optional(),
    placeholder: z.string().optional(),
    help: z.string().optional(),
    /** Se muestra solo cuando otro campo (o el contexto, p. ej. `caseTypeCode`) vale `equals` (o uno de `equals` si es lista). */
    showWhen: z.object({ field: z.string(), equals: z.unknown() }).optional(),
});
/** PUT /workflows/forms body. */
export const formDefinitionSchema = z.object({ code: codeSchema(60), name: z.string().min(1), fields: z.array(formFieldSchema) });
/**
 * Tipos de campo que un formulario de ingreso puede usar: los que una persona llena antes de que el caso exista
 * (sin archivos, firma ni catálogos internos como usuarios, áreas o juzgados). Vale para el portal y el backoffice.
 */
export const INTAKE_FIELD_TYPES = ['Text', 'Textarea', 'Number', 'Currency', 'Date', 'DateTime', 'Select', 'MultiSelect', 'Checkbox', 'Radio', 'Address', 'Email', 'Phone'];
/** Por qué un formulario no sirve como formulario de ingreso de un tipo de caso; `null` si sirve. */
export function intakeFormProblem(fields) {
    const bad = fields.filter((f) => !INTAKE_FIELD_TYPES.includes(f.type) || f.optionsFrom);
    if (!bad.length)
        return null;
    return `Tiene campos que no se pueden llenar al radicar: ${bad.map((f) => `«${f.label}»`).join(', ')}.`;
}
/** `showWhen: { field, equals }` se evalúa sobre los valores y luego sobre el contexto (p. ej. `caseTypeCode`). */
export function isFormFieldVisible(f, values, context = {}) {
    if (!f.showWhen)
        return true;
    const v = values[f.showWhen.field] ?? context[f.showWhen.field];
    return Array.isArray(f.showWhen.equals) ? f.showWhen.equals.includes(v) : v === f.showWhen.equals;
}
export const FORM_FIELD_REQUIRED = 'Este campo es obligatorio';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TEXT_MAX = 1000;
const TEXTAREA_MAX = 10_000;
const isEmpty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
const asNumber = (v) => {
    if (typeof v === 'number')
        return Number.isFinite(v) ? v : undefined;
    if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)))
        return Number(v);
    return undefined;
};
const validDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(new Date(`${s}T00:00:00Z`).getTime());
/**
 * Valida y limpia lo que una persona llenó en un formulario: obligatorios, tipo de cada valor, opciones y campos
 * condicionales (`showWhen`). Devuelve solo los campos visibles y conocidos, sin los vacíos, y un error por campo en
 * las palabras que ve la persona. La misma regla corre en el navegador (antes de enviar) y en el API (al guardar).
 */
export function validateFormData(fields, data, context = {}) {
    const raw = data ?? {};
    const values = {};
    const errors = {};
    for (const f of fields) {
        if (!isFormFieldVisible(f, raw, context))
            continue;
        const v = raw[f.name];
        if (isEmpty(v) || (f.type === 'Checkbox' && v !== true)) {
            if (f.required)
                errors[f.name] = FORM_FIELD_REQUIRED;
            else if (f.type === 'Checkbox' && v === false)
                values[f.name] = false;
            continue;
        }
        const checked = checkValue(f, v);
        if ('error' in checked)
            errors[f.name] = checked.error;
        else
            values[f.name] = checked.value;
    }
    return { values, errors };
}
const options = (f) => (f.optionsFrom ? null : f.options ?? []);
function checkValue(f, v) {
    switch (f.type) {
        case 'Number':
        case 'Currency': {
            const n = asNumber(v);
            if (n === undefined)
                return { error: 'Escribe un número' };
            if (f.type === 'Currency' && n < 0)
                return { error: 'El valor no puede ser negativo' };
            return { value: n };
        }
        case 'Checkbox':
            return v === true ? { value: true } : { error: 'Valor no válido' };
        case 'Date':
            return typeof v === 'string' && validDate(v) ? { value: v } : { error: 'Fecha no válida' };
        case 'DateTime': {
            if (typeof v !== 'string' || Number.isNaN(new Date(v).getTime()))
                return { error: 'Fecha y hora no válidas' };
            return { value: new Date(v).toISOString() };
        }
        case 'Select':
        case 'Radio': {
            if (typeof v !== 'string')
                return { error: 'Valor no válido' };
            const opts = options(f);
            if (opts && opts.length && !opts.includes(v))
                return { error: 'Elige una de las opciones' };
            return { value: v };
        }
        case 'MultiSelect':
        case 'File': {
            if (!Array.isArray(v) || v.some((x) => typeof x !== 'string'))
                return { error: 'Valor no válido' };
            const opts = f.type === 'MultiSelect' ? options(f) : null;
            if (opts && opts.length && v.some((x) => !opts.includes(x)))
                return { error: 'Elige entre las opciones' };
            return { value: [...new Set(v)] };
        }
        case 'Email': {
            if (typeof v !== 'string')
                return { error: 'Valor no válido' };
            const s = v.trim().toLowerCase();
            return EMAIL_RE.test(s) && s.length <= 320 ? { value: s } : { error: 'Correo no válido' };
        }
        default: {
            if (typeof v !== 'string')
                return { error: 'Valor no válido' };
            const s = v.trim();
            const max = f.type === 'Textarea' ? TEXTAREA_MAX : TEXT_MAX;
            if (s.length > max)
                return { error: `Máximo ${max.toLocaleString('es-CO')} caracteres` };
            return s ? { value: s } : { error: FORM_FIELD_REQUIRED };
        }
    }
}
/** Lo que el formulario de ingreso del tipo guardó en el caso (`cases.metadata.formData`). */
export function formDataOf(metadata) {
    const v = metadata?.formData;
    return v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length ? v : null;
}
//# sourceMappingURL=form.js.map